import type { ConfigField, MessagingProviderSummary } from '@chukua/common';
import { initialConfigValues, type ConfigFormValues } from './config-form-values.policy';

/** What an SMS or WhatsApp installation has in common, as far as these rules go. */
interface MessagingInstallation {
  providerKey: string;
  displayName: string;
  isSending: boolean;
  config: Record<string, string | number | boolean>;
}

/** What a messaging section says above its list: whether anything sends, and through which provider. */
export type SendingState<I> = { kind: 'none-installed' } | { kind: 'none-sending' } | { kind: 'sending'; integration: I };

export function sendingState<I extends MessagingInstallation>(integrations: I[]): SendingState<I> {
  const sending = integrations.find((integration) => integration.isSending);
  if (sending) return { kind: 'sending', integration: sending };
  return integrations.length === 0 ? { kind: 'none-installed' } : { kind: 'none-sending' };
}

/** A provider installed once already can't be installed again; its row opens its settings instead. */
export function installableProviders(providers: MessagingProviderSummary[], integrations: MessagingInstallation[]): MessagingProviderSummary[] {
  const installed = new Set(integrations.map((integration) => integration.providerKey));
  return providers.filter((provider) => !installed.has(provider.key));
}

/**
 * Whether the settings form changes the credentials, which the API checks with the provider: a secret typed in, or a
 * non-secret value that differs from the saved one. A rename alone asks the provider nothing.
 */
export function changesCredentials(fields: ConfigField[], saved: MessagingInstallation, values: ConfigFormValues): boolean {
  return fields.some((field) => {
    const value = values[field.name];
    if (field.secret) return value !== undefined && value !== null && value !== '';
    return (value ?? '') !== (saved.config[field.name] ?? '');
  });
}

/** An install starts from the provider's name; settings from what is saved. Secrets are never prefilled. */
export function providerFormInitialValues(provider: MessagingProviderSummary, saved: MessagingInstallation | undefined): ConfigFormValues {
  return { displayName: saved?.displayName ?? provider.displayName, ...initialConfigValues(provider.fields, saved?.config ?? {}) };
}
